import { useCallback } from 'react';
import { generatePath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';

import ProductClass from '../../types/ProductClass';
import { addParametersToPath, removeQueryParameters } from '../../util/queryString';
import useQuery from '../routes/useQuery';

/**
 * @param {Object} props
 * @param {boolean} props.productPage
 * @param {boolean} props.checkoutPage
 * @returns
 */
const useActiveProduct = ({ productPage, checkoutPage }) => {
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const params = useParams();
  const queryParams = useQuery();

  /**
   * @param {String[]} params
   */
  const setQueryParams = (params) =>
    removeQueryParameters({
      location,
      params,
    });

  const getActiveProductPath = useCallback(({activeProductId, queryParams}) => {
    // Store all query params to reinsert back the remaining params we want to preserve in the url
    // after history.replace
    const allQueryParams = setQueryParams(['columnId', 'rowId']);
    const pathParams = { ...params, activeProductId };

    const newProductPath = addParametersToPath(
      generatePath(match.path, pathParams),
      {
        ...queryParams,
        ...allQueryParams,
      }
    );

    return newProductPath;
  }, [match]);

  const setActiveProductId = useCallback(({ activeProductId, queryParams }) => {
    const newProductPath = getActiveProductPath({activeProductId, queryParams});

    if (activeProductId && productPage) {
      history.replace(newProductPath);
    } else if (activeProductId && checkoutPage) {
      history.replace(newProductPath);
      return activeProductId;
    } else {
      history.replace(newProductPath);
    }
  }, [history]);

  const getActiveCollectionElementIds = useCallback(({ product, activeProductId }) => {
    if (product.class === ProductClass.COLLECTION) {
      if (activeProductId) {
        let activeItem =
          product.collection.getItemWithProductId(activeProductId);
        if (activeItem) {
          return {
            columnId: activeItem.column_id,
            rowId: activeItem.row_id,
          };
        }
      }

      const columnId = queryParams.get('columnId');
      const rowId = queryParams.get('rowId');

      return {
        columnId: columnId ? Number(columnId) : null,
        rowId: rowId ? Number(rowId) : null,
      };
    }

    return {
      columnId: null,
      rowId: null,
    };
  }, [queryParams.size]);

  return { setActiveProductId, getActiveCollectionElementIds, getActiveProductPath };
};

export default useActiveProduct;
